import type * as Sre from "speech-rule-engine/js/common/system.js";

export type SpeechEngine = typeof Sre;

const LOCALE_MAPS: Record<string, () => Promise<{ default: unknown }>> = {
  base: () => import("speech-rule-engine/lib/mathmaps/base.json"),
  en: () => import("speech-rule-engine/lib/mathmaps/en.json"),
};

async function loadLocale(locale: string): Promise<string> {
  const loadMap = LOCALE_MAPS[locale];
  if (!loadMap) {
    throw new Error(`No speech rules bundled for locale "${locale}"`);
  }
  const { default: map } = await loadMap();
  return JSON.stringify(map);
}

let speechEngine: Promise<SpeechEngine> | undefined;

async function setUpEngine(): Promise<SpeechEngine> {
  // The engine fetches its rules from jsDelivr as soon as it is imported, so the loader has to be
  // in place before the import. It reads the global `SREfeature` on every setup.
  (globalThis as { SREfeature?: Record<string, unknown> }).SREfeature = { custom: loadLocale };
  // The package root also loads the engine's CLI, which requires Node's `process`.
  const engine = await import("speech-rule-engine/js/common/system.js");
  await engine.setupEngine({ locale: "en", domain: "clearspeak", modality: "speech" });
  await engine.engineReady();
  return engine;
}

/**
 * Speech Rule Engine (the engine behind MathJax's speech), set up for English ClearSpeak.
 * Loaded once, on first call: the engine and its rules weigh about 1 MB.
 */
export function loadSpeechEngine(): Promise<SpeechEngine> {
  speechEngine ??= setUpEngine().catch((error: unknown) => {
    speechEngine = undefined;
    throw error;
  });
  return speechEngine;
}
